// src/telemetry/appinsights.js
const ACS_BROKER_HOST = 'api.flightproxy.skype.com';
const THIRD_PARTY_ANALYTICS_DEPENDENCIES = [
  { host: 'www.google-analytics.com', path: '/g/collect' },
  { host: 'analytics.google.com', path: '/g/collect' },
  { host: 'www.google.com', path: '/ccm/' }
];
const ACS_AUTO_DEPENDENCY_HOSTS = [
  'api.flightproxy.skype.com',
  'go.trouter.communication.microsoft.com',
  'registrar.communication.microsoft.com',
  'ecs.communication.microsoft.com',
  'auth.relay.communication.microsoft.com',
  'ic3.events.data.microsoft.com'
];

let appInsightsPromise;
let appInsightsUnavailable = false;
let userContextGeneration = 0;

function isSuccessfulStatus(responseCode) {
  const status = Number(responseCode);
  return status >= 200 && status < 300;
}

function getDependencyText(baseData) {
  return [
    baseData?.data,
    baseData?.target,
    baseData?.name,
    baseData?.commandName
  ].filter(Boolean).join(' ');
}

function isNoisySuccessfulAcsBrokerDependency(item) {
  const baseType = item?.baseType || item?.data?.baseType;
  if (baseType !== 'RemoteDependencyData') return false;

  const baseData = item?.baseData || item?.data?.baseData;
  const dependencyText = getDependencyText(baseData);
  if (!dependencyText.includes(ACS_BROKER_HOST)) return false;

  const isBrokerSubscribe =
    dependencyText.includes('/api/v2/cp/') &&
    dependencyText.includes('broker.skype.com/api/v1/subscribe/');
  if (!isBrokerSubscribe) return false;

  const successful =
    baseData?.success === true ||
    String(baseData?.success).toLowerCase() === 'true' ||
    isSuccessfulStatus(baseData?.responseCode);

  return successful;
}

function isThirdPartyAnalyticsDependency(item) {
  const baseType = item?.baseType || item?.data?.baseType;
  if (baseType !== 'RemoteDependencyData') return false;

  const baseData = item?.baseData || item?.data?.baseData;
  const dependencyText = getDependencyText(baseData);
  return THIRD_PARTY_ANALYTICS_DEPENDENCIES.some(({ host, path }) => (
    dependencyText.includes(host) && dependencyText.includes(path)
  ));
}

function getAppInsights() {
  if (appInsightsUnavailable) return Promise.resolve(null);

  if (!appInsightsPromise) {
    appInsightsPromise = import('@microsoft/applicationinsights-web')
      .then(({ ApplicationInsights }) => {
        const ai = new ApplicationInsights({
          config: {
            connectionString: process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING,
            enableAutoRouteTracking: true,
            samplingPercentage: 100,
            excludeRequestFromAutoTrackingPatterns: ACS_AUTO_DEPENDENCY_HOSTS.map(
              (host) => new RegExp(`^https://[^/]*${host.replace(/\./g, '\\.')}(?:/|$)`, 'i')
            ),
          },
        });

        ai.addTelemetryInitializer((item) => {
          if (isNoisySuccessfulAcsBrokerDependency(item)) return false;
          if (isThirdPartyAnalyticsDependency(item)) return false;
          return true;
        });

        ai.loadAppInsights();
        return ai;
      })
      .catch((error) => {
        appInsightsUnavailable = true;
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Application Insights could not be initialized.', error);
        }
        return null;
      });
  }

  return appInsightsPromise;
}

/** Hash email to avoid sending PII */
async function sha256Hex(text) {
  try {
    const enc = new TextEncoder().encode(text);
    const buf = await crypto.subtle.digest('SHA-256', enc);
    return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    // Fallback (non-cryptographic) if SubtleCrypto is unavailable (older browsers)
    let hash = 0;
    for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) | 0;
    return `fallback_${(hash >>> 0).toString(16)}`;
  }
}

// privacy: NEVER send raw email. Hash it if you want cohorting.
export async function setUser(userEmail) {
  if (!userEmail) return;
  const normalizedEmail = String(userEmail).trim().toLowerCase();
  const generation = ++userContextGeneration;

  try {
    const [ai, hash] = await Promise.all([
      getAppInsights(),
      sha256Hex(normalizedEmail)
    ]);
    const currentEmail = String(localStorage.getItem('userEmail') || '').trim().toLowerCase();
    if (generation !== userContextGeneration || currentEmail !== normalizedEmail) return;
    ai?.setAuthenticatedUserContext(hash);
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('Application Insights user context could not be set.', error);
    }
  }
}

export async function clearUserContext() {
  userContextGeneration += 1;
  try {
    if (!appInsightsPromise) return;
    const ai = await getAppInsights();
    ai?.clearAuthenticatedUserContext?.();
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('Application Insights user context could not be cleared.', error);
    }
  }
}

const send = (callback) => {
  getAppInsights()
    .then((ai) => {
      if (!ai) return;
      callback(ai);
    })
    .catch((error) => {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Application Insights telemetry call failed.', error);
      }
    });
};

export const track = {
  pv: (name, props = {}) => {
    send((ai) => ai.trackPageView({ name, uri: window.location.href }, props));
  },
  ev: (name, props = {}) => {
    send((ai) => ai.trackEvent({ name }, props));
  },
  ex: (error, props = {}) => {
    send((ai) => ai.trackException({ exception: error }, props));
  },
};

const appInsightsApi = {
  getAppInsights,
  setUser,
  clearUserContext,
  track,
};

export default appInsightsApi;
